import { supabase } from '../lib/supabase';
import type { Todo, CreateTodoInput, UpdateTodoInput, TodoGroup, TodoWithSource, TodoSource } from '../types/todo';
import { checkAndAwardBadges } from './gamificationService';
import { logger } from '../lib/logger';

export interface GroupedTodos {
  group: TodoGroup;
  todos: Todo[];
}

export interface GroupedTodosWithSource {
  group: TodoGroup;
  todos: TodoWithSource[];
}

function getStartOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getEndOfWeek(date: Date): Date {
  const d = new Date(date);
  const dayOfWeek = d.getDay();
  const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
  d.setDate(d.getDate() + daysUntilSunday);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function categorizeTodo(dueDate: string, completed: boolean): TodoGroup {
  if (completed) {
    return 'COMPLETED';
  }

  const today = getStartOfDay(new Date());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const endOfWeek = getEndOfWeek(today);

  const dueDateObj = getStartOfDay(new Date(dueDate));

  if (dueDateObj < today) {
    return 'OVERDUE';
  }

  if (dueDateObj.getTime() === today.getTime()) {
    return 'TODAY';
  }

  if (dueDateObj.getTime() === tomorrow.getTime()) {
    return 'TOMORROW';
  }

  if (dueDateObj <= endOfWeek) {
    return 'THIS_WEEK';
  }

  return 'LATER';
}

export function groupTodos(todos: Todo[]): GroupedTodos[] {
  const groups: Record<TodoGroup, Todo[]> = {
    OVERDUE: [],
    TODAY: [],
    TOMORROW: [],
    THIS_WEEK: [],
    LATER: [],
    COMPLETED: [],
  };

  todos.forEach((todo) => {
    const group = categorizeTodo(todo.due_date, todo.completed);
    groups[group].push(todo);
  });

  const groupOrder: TodoGroup[] = ['OVERDUE', 'TODAY', 'TOMORROW', 'THIS_WEEK', 'LATER', 'COMPLETED'];

  return groupOrder
    .filter((group) => groups[group].length > 0)
    .map((group) => ({
      group,
      todos: groups[group].sort((a, b) => {
        if (a.completed !== b.completed) {
          return a.completed ? 1 : -1;
        }
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      }),
    }));
}

export async function getTodos(userId: string): Promise<Todo[]> {
  const { data, error } = await supabase
    .from('todos')
    .select('*')
    .eq('user_id', userId)
    .order('due_date', { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function getTodosForToday(userId: string): Promise<Todo[]> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const { data, error } = await supabase
    .from('todos')
    .select('*')
    .eq('user_id', userId)
    .eq('completed', false)
    .gte('due_date', today.toISOString().split('T')[0])
    .lt('due_date', tomorrow.toISOString().split('T')[0])
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function getOverdueTodos(userId: string): Promise<Todo[]> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const { data, error } = await supabase
    .from('todos')
    .select('*')
    .eq('user_id', userId)
    .eq('completed', false)
    .lt('due_date', today.toISOString().split('T')[0])
    .order('due_date', { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function createTodo(userId: string, input: CreateTodoInput): Promise<Todo> {
  const { data, error } = await supabase
    .from('todos')
    .insert({
      ...input,
      user_id: userId,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateTodo(
  todoId: string,
  userId: string,
  input: UpdateTodoInput
): Promise<Todo> {
  const { data, error } = await supabase
    .from('todos')
    .update({
      ...input,
      updated_at: new Date().toISOString(),
    })
    .eq('id', todoId)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function toggleTodoCompleted(
  todoId: string,
  userId: string,
  completed: boolean
): Promise<Todo> {
  const { data, error } = await supabase
    .from('todos')
    .update({
      completed,
      updated_at: new Date().toISOString(),
    })
    .eq('id', todoId)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw error;

  // Check for new badges when completing a todo (non-blocking)
  if (completed) {
    checkAndAwardBadges(userId, 'todos').catch((err) => {
      logger.warn('Failed to check todo badges:', err);
    });
  }

  return data;
}

export async function deleteTodo(todoId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('todos')
    .delete()
    .eq('id', todoId)
    .eq('user_id', userId);

  if (error) throw error;
}

/**
 * Keeps only the last 5 completed todos and deletes older ones.
 * Completed todos are sorted by updated_at (when they were marked complete).
 */
export async function cleanupOldCompletedTodos(userId: string): Promise<void> {
  // Get all completed todos, sorted by updated_at descending (newest first)
  const { data: completedTodos, error: fetchError } = await supabase
    .from('todos')
    .select('id, updated_at')
    .eq('user_id', userId)
    .eq('completed', true)
    .order('updated_at', { ascending: false });

  if (fetchError) throw fetchError;

  // If we have more than 5 completed todos, delete the older ones
  if (completedTodos && completedTodos.length > 5) {
    const todosToDelete = completedTodos.slice(5).map(t => t.id);

    const { error: deleteError } = await supabase
      .from('todos')
      .delete()
      .in('id', todosToDelete)
      .eq('user_id', userId);

    if (deleteError) throw deleteError;
  }
}

export async function enrichTodosWithSource(todos: Todo[], userId: string): Promise<TodoWithSource[]> {
  // Group todos by source type
  const leadIds = todos.filter(t => t.source_type === 'lead' && t.source_id).map(t => t.source_id!);
  const productIds = todos.filter(t => t.source_type === 'product' && t.source_id).map(t => t.source_id!);
  const formIds = todos.filter(t => t.source_type === 'form' && t.source_id).map(t => t.source_id!);
  const websiteIds = todos.filter(t => t.source_type === 'website' && t.source_id).map(t => t.source_id!);
  const mediaIds = todos.filter(t => t.source_type === 'media' && t.source_id).map(t => t.source_id!);

  // Fetch source data in parallel
  const [leads, products, forms, websites, mediaItems] = await Promise.all([
    leadIds.length > 0
      ? supabase.from('leads').select('id, full_name, email').in('id', leadIds).eq('user_id', userId)
      : { data: [] },
    productIds.length > 0
      ? supabase.from('products').select('id, name, sku, slug').in('id', productIds)
      : { data: [] },
    formIds.length > 0
      ? supabase.from('forms').select('id, name').in('id', formIds).eq('user_id', userId)
      : { data: [] },
    websiteIds.length > 0
      ? supabase.from('site_pages').select('id, title, type').in('id', websiteIds).eq('user_id', userId)
      : { data: [] },
    mediaIds.length > 0
      ? supabase.from('media').select('id, filename').in('id', mediaIds).eq('user_id', userId)
      : { data: [] },
  ]);

  // Build lookup maps
  const leadMap = new Map((leads.data || []).map(l => [l.id, l]));
  const productMap = new Map((products.data || []).map(p => [p.id, p]));
  const formMap = new Map((forms.data || []).map(f => [f.id, f]));
  const websiteMap = new Map((websites.data || []).map(w => [w.id, w]));
  const mediaMap = new Map((mediaItems.data || []).map(m => [m.id, m]));

  // Enrich todos with source data
  return todos.map(todo => {
    if (!todo.source_type || !todo.source_id) {
      return todo;
    }

    let source: TodoSource | undefined;

    switch (todo.source_type) {
      case 'lead': {
        const lead = leadMap.get(todo.source_id);
        if (lead) {
          source = {
            type: 'lead',
            id: todo.source_id,
            name: lead.full_name,
            subtitle: lead.email || undefined,
          };
        }
        break;
      }
      case 'product': {
        const product = productMap.get(todo.source_id);
        if (product) {
          source = {
            type: 'product',
            id: todo.source_id,
            name: product.name,
            subtitle: `Art.-Nr.: ${product.sku}`,
            slug: product.slug,
          };
        }
        break;
      }
      case 'form': {
        const form = formMap.get(todo.source_id);
        if (form) {
          source = {
            type: 'form',
            id: todo.source_id,
            name: form.name,
          };
        }
        break;
      }
      case 'website': {
        const website = websiteMap.get(todo.source_id);
        if (website) {
          source = {
            type: 'website',
            id: todo.source_id,
            name: website.title,
            subtitle: website.type === 'BUSINESS' ? 'Business Page' : 'Products Page',
          };
        }
        break;
      }
      case 'media': {
        const media = mediaMap.get(todo.source_id);
        if (media) {
          source = {
            type: 'media',
            id: todo.source_id,
            name: media.filename,
          };
        }
        break;
      }
    }

    return { ...todo, source };
  });
}

export async function getTodosWithSource(userId: string): Promise<TodoWithSource[]> {
  const todos = await getTodos(userId);
  return enrichTodosWithSource(todos, userId);
}

export type LeadSource = 'manual' | 'form';

export interface CreateTodoForLeadOptions {
  userId: string;
  leadId: string;
  leadName: string;
  source: LeadSource;
  formName?: string;
}

export async function createTodoForNewLead(options: CreateTodoForLeadOptions): Promise<Todo> {
  const { userId, leadId, leadName, source, formName } = options;

  const today = new Date().toISOString().split('T')[0];

  const title = `Lead ${leadName} bearbeiten`;
  const description = source === 'manual'
    ? 'Manuell angelegt'
    : `Über Formular: ${formName || 'Unbekannt'}`;

  return createTodo(userId, {
    title,
    description,
    due_date: today,
    tag: 'lead',
    source_type: 'lead',
    source_id: leadId,
  });
}

export function groupTodosWithSource(todos: TodoWithSource[]): GroupedTodosWithSource[] {
  const groups: Record<TodoGroup, TodoWithSource[]> = {
    OVERDUE: [],
    TODAY: [],
    TOMORROW: [],
    THIS_WEEK: [],
    LATER: [],
    COMPLETED: [],
  };

  todos.forEach((todo) => {
    const group = categorizeTodo(todo.due_date, todo.completed);
    groups[group].push(todo);
  });

  const groupOrder: TodoGroup[] = ['OVERDUE', 'TODAY', 'TOMORROW', 'THIS_WEEK', 'LATER', 'COMPLETED'];

  return groupOrder
    .filter((group) => groups[group].length > 0)
    .map((group) => {
      let groupTodos = groups[group].sort((a, b) => {
        if (a.completed !== b.completed) {
          return a.completed ? 1 : -1;
        }
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      });

      // Only show last 5 completed todos in UI (but keep all in DB for badge counting)
      if (group === 'COMPLETED') {
        // Sort by updated_at descending (newest first) and take last 5
        groupTodos = groupTodos
          .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
          .slice(0, 5);
      }

      return { group, todos: groupTodos };
    });
}
